<template>
  <el-button
    :type="elType"
    :plain="plain"
    :loading="loading"
    :disabled="disabled"
    :size="elSize"
    :native-type="nativeType"
    :tag="elTag"
    :href="href"
    :class="['app-btn', `app-btn--${variant}`, sizeClass]"
    @click="$emit('click', $event)"
  >
    <NuxtIcon v-if="icon && !loading" :name="icon" class="app-btn__icon" />
    <slot />
  </el-button>
</template>

<script setup lang="ts">
/**
 * AppButton — single source of truth for buttons across the app.
 *
 * Wraps el-button so migrating native <button> → <AppButton> is a
 * find-and-replace. The variant prop maps to EP's `type` so design
 * tokens stay consistent and we don't pepper raw `type="primary"` /
 * custom CSS classes across hundreds of files.
 *
 * Variants:
 *   - primary    → solid cyan (default actions)
 *   - secondary  → outlined neutral (next-to-primary actions)
 *   - ghost      → text-only, hover-fill (low-emphasis)
 *   - danger     → solid red (destructive)
 *   - danger-outline → outlined red (destructive but secondary)
 *   - success    → solid green (positive confirms)
 *
 * Size: 'sm' | 'md' | 'lg' (defaults to 'md' for app standard 32px)
 */
type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-outline' | 'success'
type Size = 'sm' | 'md' | 'lg'

const props = withDefaults(defineProps<{
  variant?: Variant
  size?: Size
  icon?: string
  loading?: boolean
  disabled?: boolean
  nativeType?: 'button' | 'submit' | 'reset'
  tag?: string
  href?: string
}>(), {
  variant: 'secondary',
  size: 'md',
  nativeType: 'button',
  tag: 'button'
})

defineEmits<{ click: [event: MouseEvent] }>()

// `tag="NuxtLink"` arrives as a plain string, and el-button hands it to
// <component :is>, which rendered an unknown <nuxtlink> element: no
// anchor, no navigation (the dead Settings button on /mail). Resolve the
// string to the real component; NuxtLink takes `href` as an alias of `to`.
const NuxtLinkComponent = resolveComponent('NuxtLink')
const elTag = computed(() => (props.tag === 'NuxtLink' || props.tag === 'router-link' ? NuxtLinkComponent : props.tag))

// Map our variant vocabulary → EP's type prop. We always set
// `plain` separately for outline variants so EP renders the
// bordered shape but our theme.css picks the colors.
const elType = computed(() => {
  switch (props.variant) {
    case 'primary': return 'primary'
    case 'secondary': return 'default'
    case 'ghost': return 'default'
    case 'danger': return 'danger'
    case 'danger-outline': return 'danger'
    case 'success': return 'success'
    default: return 'default'
  }
})

// Outline variants → forward EP's `plain` prop. Currently only
// danger-outline opts in; primary-outline could be added as a
// future variant if needed (and would set isOutline=true too).
const plain = computed(() => props.variant === 'danger-outline')

const elSize = computed(() => {
  if (props.size === 'sm') return 'small'
  if (props.size === 'lg') return 'large'
  return 'default'
})
const sizeClass = computed(() => `app-btn--${props.size}`)
</script>

<!-- NOT scoped: el-button mounts a <button> inside its own shadow-
     like tree; scoped-CSS data attribute doesn't propagate to that
     internal button, so .app-btn--ghost styling wouldn't apply.
     Using a global block ensures the variant class actually styles
     the rendered button. Class names are app-prefixed so collision
     risk with EP internals is essentially zero. -->
<style>
.el-button.app-btn {
  font-weight: 500;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.el-button.app-btn--ghost {
  background: transparent !important;
  border-color: transparent !important;
  color: var(--text-muted) !important;
}
/* Ghost hover uses a CYAN tint instead of pure white. The old
 * `rgba(255,255,255,0.06)` background + `color:#fff` made a hover
 * state on a transparent button look like a random white box
 * appearing — and clashed with the neighbouring secondary button's
 * already-whitish chrome. Cyan tint ties the hover into the brand
 * palette and matches the primary CTA next to it. */
.el-button.app-btn--ghost:hover {
  background: rgba(0, 242, 255, 0.08) !important;
  border-color: transparent !important;
  color: var(--primary) !important;
}
.el-button.app-btn--ghost:focus-visible {
  outline: 2px solid var(--primary);
  outline-offset: 2px;
}
/* Secondary variant — explicit override so we don't depend on EP's
 * default rules. Subtle outlined shape at rest; cyan tint on hover
 * to match the ghost/primary hover language. Previously this fell
 * through to the global `.el-button:not([class*=el-button--])`
 * rule which used pure-white hover tints — looked stark next to
 * the cyan primary button. */
.el-button.app-btn--secondary {
  --el-button-bg-color: rgba(255, 255, 255, 0.04) !important;
  --el-button-border-color: rgba(255, 255, 255, 0.12) !important;
  --el-button-text-color: var(--text-main) !important;
  --el-button-hover-bg-color: rgba(0, 242, 255, 0.10) !important;
  --el-button-hover-border-color: rgba(0, 242, 255, 0.45) !important;
  --el-button-hover-text-color: var(--primary) !important;
  --el-button-active-bg-color: rgba(0, 242, 255, 0.16) !important;
}
.app-btn .app-btn__icon {
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
  font-size: 14px;
}
</style>
